<template>
  <section class="py-9 tablet:py-20">
    <div class="container-wrap">
      <div class="mb-4 desktop:mb-0">
        <div class="desktop:flex desktop:gap-6">
          <div class="desktop:w-1/2">
            <h2 class="text-h2 mb-10 tablet:line-clamp-2">
              {{ title }}
            </h2>
            <TheRichText
              v-if="descriptionHtml || description"
              class="mb-6 w-full text-sm leading-6 text-gray-700 tablet:mb-10 tablet:line-clamp-7 tablet:text-base"
              :text="descriptionHtml || description"
              :is-html="!!descriptionHtml"
            />

            <div v-if="thumbnail" class="mb-6 w-full">
              <TheMedia
                :title="title"
                :description="description || ''"
                :video-data="wistiaCode ? video : undefined"
                :thumbnail="thumbnail"
                :theme="theme"
                class="aspect-video w-full rounded-3xl"
              />
            </div>

            <div v-if="isDesktop" class="flex gap-4">
              <TheButton
                v-if="button01?.text"
                :id="button01?.trackingCode"
                :to="button01.link"
                :title="button01.text"
                class="w-1/2"
              >
                <span class="font-bold">
                  {{ button01.text }}
                </span>
              </TheButton>
              <TheButton
                v-if="button02?.text"
                :id="button02?.trackingCode"
                :to="button02.link"
                :title="button02.text"
                type="outline"
                class="w-1/2"
              >
                <span class="font-bold">
                  {{ button02.text }}
                </span>
              </TheButton>
            </div>
          </div>
          <div class="desktop:w-1/2">
            <ul>
              <li
                v-for="item in items"
                :key="item.name"
                class="border-b border-solid border-gray-500 py-5 first:pt-0"
              >
                <h3 class="text-h5 mb-2">{{ item.name }}</h3>
                <TheRichText
                  class="text-sm leading-6 text-gray-700"
                  :text="item.descriptionHtml || item.description"
                  :is-html="!!item.descriptionHtml"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div
        v-if="!isDesktop"
        class="flex flex-col gap-4 tablet:flex-row tablet:justify-center"
      >
        <TheButton
          v-if="button01?.text"
          :id="button01?.trackingCode"
          :to="button01.link"
          :title="button01.text"
          class="w-full tablet:w-60"
        >
          <span class="font-bold">
            {{ button01.text }}
          </span>
        </TheButton>
        <TheButton
          v-if="button02?.text"
          :id="button02?.trackingCode"
          :to="button02.link"
          :title="button02.text"
          type="outline"
          class="w-full tablet:w-60"
        >
          <span class="font-bold">
            {{ button02.text }}
          </span>
        </TheButton>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  /**
   * @see https://www.figma.com/design/QJ3ZvEbUHI9xI11iudYf8U/Advantech-WISE-Library?node-id=12796-23823&t=89PIFDiQEC4zcSbf-0
   */

  import type { IButton, TTheme } from '@/assets/types/Main.global';
  import type { IVideoData } from '@/components/Shared/Lightbox/Video.vue';

  interface IItem {
    name: string;
    description?: string;
    descriptionHtml?: string;
  }

  interface IProps {
    title: string;
    description?: string;
    descriptionHtml?: string;
    items?: IItem[];
    thumbnail?: string;
    wistiaCode?: string;
    button01?: IButton | null;
    button02?: IButton | null;
    theme?: TTheme;
  }

  const props = withDefaults(defineProps<IProps>(), {
    description: '',
    descriptionHtml: '',
    items: () => [],
    thumbnail: '',
    wistiaCode: '',
    button01: null,
    button02: null,
    theme: 'blue',
  });

  const { isDesktop } = useScreen();

  const video = computed(
    (): IVideoData => ({
      title: props.title,
      description: props.description,
      url: props.wistiaCode,
      imageUrl: props.thumbnail,
    })
  );

  export type { IItem };
</script>
